import React, { useState, useMemo } from "react";
import Style from "./ChangeProduct.module.css";
import { separate } from "../../Functions/NumberSeprator";
import toEnglishDigits from "../../Functions/PersionToEn";
import {
  Search,
  XLg,
  ArrowRepeat,
  CheckCircleFill,
  BoxSeam,
  ExclamationCircle,
} from "react-bootstrap-icons";

const ChangeProduct = ({
  searchValue, // Array of inventory products
  setChangeProduct,
  handleClose,
  rawInfo,
  isLoading = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const initialCount = Number(rawInfo?.newCount ?? rawInfo?.count ?? 1);
  const [count, setCount] = useState(initialCount);
  const [errorMsg, setErrorMsg] = useState("");

  // Filter products by search term safely
  const filteredProducts = useMemo(() => {
    if (!Array.isArray(searchValue)) return [];
    const term = searchTerm.trim().toLowerCase();
    if (!term) return searchValue.slice(0, 30); // show top 30 initially for speed
    return searchValue.filter(
      (item) =>
        item.name && item.name.toLowerCase().includes(term)
    );
  }, [searchValue, searchTerm]);

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setErrorMsg("");
  };

  const handleCountChange = (val) => {
    const nextVal = Math.max(1, Number(val) || 1);
    setCount(nextVal);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedProduct) {
      setErrorMsg("لطفاً یک محصول را از لیست انبار انتخاب کنید.");
      return;
    }

    setChangeProduct({
      product_id: selectedProduct.id,
      p_product_id: rawInfo?.id,
      new_count: Number(count),
      new_price: selectedProduct.price || 0,
      availability: "1",
      selectedProduct: selectedProduct,
    });
    handleClose();
  };

  return (
    <div className={Style.wrapper}>
      {/* Original Product Target Banner */}
      <div className={Style.targetCard}>
        <div className={Style.targetHeader}>
          <ArrowRepeat className={Style.targetIcon} />
          <div className={Style.targetTextGroup}>
            <span className={Style.targetLabel}>کالای در حال جایگزینی</span>
            <h4 className={Style.targetName}>{rawInfo?.name || "بدون نام"}</h4>
          </div>
        </div>
        <div className={Style.targetBadge}>
          تعداد در فاکتور: {rawInfo?.count || 1} عدد
        </div>
      </div>

      <form onSubmit={handleSubmit} className={Style.form}>
        {/* Selected Product Banner if already chosen */}
        {selectedProduct ? (
          <div className={Style.selectedBanner}>
            <div className={Style.selectedImgHolder}>
              <img
                src={`https://cdn.barest.ir/product/m/${selectedProduct.id}.jpg`}
                alt={selectedProduct.name}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
              <BoxSeam className={Style.fallbackIcon} />
            </div>

            <div className={Style.selectedInfo}>
              <div className={Style.selectedBadge}>
                <CheckCircleFill /> محصول انتخاب‌شده
              </div>
              <h5 className={Style.selectedTitle}>{selectedProduct.name}</h5>
              <span className={Style.selectedPrice}>
                {separate(selectedProduct.price || 0)} تومان
              </span>
            </div>

            <button
              type="button"
              className={Style.changeSelectionBtn}
              onClick={() => setSelectedProduct(null)}
            >
              تغییر محصول
            </button>
          </div>
        ) : (
          /* Search & Catalog Section */
          <div className={Style.searchSection}>
            <div className={Style.searchBar}>
              <Search className={Style.searchIcon} />
              <input
                type="text"
                className={Style.searchInput}
                placeholder="جستجوی نام محصول در انبار شما..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
              />
              {searchTerm && (
                <button
                  type="button"
                  className={Style.clearSearchBtn}
                  onClick={() => setSearchTerm("")}
                  aria-label="پاک کردن جستجو"
                >
                  <XLg />
                </button>
              )}
            </div>

            {/* Product List */}
            <div className={Style.catalogList}>
              {isLoading ? (
                <div className={Style.loadingState}>
                  <div className={Style.spinner}></div>
                  <span>در حال بارگذاری لیست محصولات انبار...</span>
                </div>
              ) : filteredProducts.length > 0 ? (
                filteredProducts.map((item) => (
                  <div
                    key={item.id}
                    className={Style.catalogItem}
                    onClick={() => handleSelectProduct(item)}
                  >
                    <div className={Style.itemImg}>
                      <img
                        src={`https://cdn.barest.ir/product/m/${item.id}.jpg`}
                        alt={item.name}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                      <BoxSeam className={Style.itemFallbackIcon} />
                    </div>
                    <div className={Style.itemDetails}>
                      <span className={Style.itemName}>{item.name}</span>
                      <span className={Style.itemPrice}>
                        {separate(item.price || 0)} تومان
                      </span>
                    </div>
                    <button type="button" className={Style.selectBtn}>
                      انتخاب
                    </button>
                  </div>
                ))
              ) : (
                <div className={Style.emptyState}>
                  <ExclamationCircle className={Style.emptyIcon} />
                  <span>محصولی مطابق با جستجوی شما در انبار یافت نشد.</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Replacement Quantity */}
        <div className={Style.fieldGroup}>
          <label className={Style.label} htmlFor="changeProductCount">
            <span>تعداد محصول جایگزین</span>
            <span className={Style.unitHint}>عدد</span>
          </label>
          <div className={Style.countControl}>
            <button
              type="button"
              className={Style.counterBtn}
              onClick={() => handleCountChange(count + 1)}
              aria-label="افزایش تعداد"
            >
              +
            </button>
            <input
              id="changeProductCount"
              type="number"
              min="1"
              className={Style.countInput}
              value={count}
              onChange={(e) => handleCountChange(toEnglishDigits(e.target.value))}
            />
            <button
              type="button"
              className={Style.counterBtn}
              onClick={() => handleCountChange(Math.max(1, count - 1))}
              aria-label="کاهش تعداد"
            >
              -
            </button>
          </div>
        </div>

        {errorMsg && (
          <div className={Style.errorNotice}>
            <ExclamationCircle />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Modal Actions */}
        <div className={Style.actions}>
          <button
            type="submit"
            className={Style.submitBtn}
            disabled={!selectedProduct}
          >
            تأیید و جایگزینی کالا
          </button>
          <button
            type="button"
            className={Style.cancelBtn}
            onClick={handleClose}
          >
            انصراف
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChangeProduct;
